import {
  DarkModeRounded as DarkModeIcon,
  LightModeRounded as LightModeIcon,
  PaletteOutlined as PaletteIcon,
} from '@mui/icons-material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useRef, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { type AppThemeMode, useAppTheme } from '@/context/app-theme-context';
import { useUserProfile } from '@/firebase/hooks/use-user-profile';

const THEME_MODES: AppThemeMode[] = ['light', 'dark'];
const CLOUD_SAVE_BUSY_WINDOW_MS = 800;

function ThemePreview({ themeMode }: { themeMode: AppThemeMode }) {
  const isLight = themeMode === 'light';
  const Icon = isLight ? LightModeIcon : DarkModeIcon;

  return (
    <Box
      aria-hidden="true"
      sx={{
        width: '100%',
        height: 96,
        p: 1.25,
        borderRadius: 2,
        bgcolor: isLight ? '#F7F5F2' : '#0F0F0F',
        border: '1px solid',
        borderColor: isLight ? '#DDD6CE' : '#303030',
        color: isLight ? '#241F1A' : '#FFFFFF',
        display: 'grid',
        gridTemplateColumns: '28% 1fr',
        gridTemplateRows: '22px 1fr',
        gap: 1,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          gridColumn: '1 / -1',
          borderRadius: 1,
          bgcolor: isLight ? '#FFFFFF' : '#1A1A1A',
          border: '1px solid',
          borderColor: isLight ? '#E4DED7' : '#2B2B2B',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          px: 0.75,
        }}
      >
        <Icon sx={{ color: isLight ? '#B45309' : '#D97706', fontSize: 15 }} />
      </Box>
      <Box
        sx={{
          borderRadius: 1,
          bgcolor: isLight ? '#EFEAE4' : '#1A1A1A',
          border: '1px solid',
          borderColor: isLight ? '#DDD6CE' : '#292929',
        }}
      />
      <Stack spacing={0.75}>
        <Box
          sx={{
            width: '58%',
            height: 7,
            borderRadius: 99,
            bgcolor: isLight ? '#B45309' : '#D97706',
          }}
        />
        <Box
          sx={{
            height: 6,
            borderRadius: 99,
            bgcolor: isLight ? '#C9C1B8' : '#3B3B3B',
          }}
        />
        <Box
          sx={{
            width: '78%',
            height: 6,
            borderRadius: 99,
            bgcolor: isLight ? '#DDD6CE' : '#303030',
          }}
        />
        <Box
          sx={{
            flexGrow: 1,
            mt: '4px !important',
            borderRadius: 1,
            bgcolor: isLight ? '#FFFFFF' : '#1A1A1A',
            border: '1px solid',
            borderColor: isLight ? '#E4DED7' : '#2B2B2B',
          }}
        />
      </Stack>
    </Box>
  );
}

export function AppearanceSettings() {
  const { t } = useTranslation();
  const { mode, setMode } = useAppTheme();
  const { updateProfile } = useUserProfile();
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const saveRequestId = useRef(0);

  const handleThemeChange = async (
    _event: MouseEvent<HTMLElement>,
    nextMode: AppThemeMode | null,
  ) => {
    if (!nextMode || nextMode === mode || isSaving || !THEME_MODES.includes(nextMode)) {
      return;
    }

    const previousMode = mode;
    const requestId = ++saveRequestId.current;
    setSaveError(null);
    setMode(nextMode);
    setIsSaving(true);

    // Firestore keeps writes queued while offline. Release the UI after a
    // short acknowledgement window so appearance remains usable meanwhile.
    const busyTimer = window.setTimeout(() => {
      if (saveRequestId.current === requestId) setIsSaving(false);
    }, CLOUD_SAVE_BUSY_WINDOW_MS);

    try {
      await updateProfile({ themeMode: nextMode });
    } catch (error) {
      console.error('Failed to save theme preference:', error);
      if (saveRequestId.current === requestId) {
        setMode(previousMode);
        setSaveError(t('profile.appearanceSaveError'));
      }
    } finally {
      window.clearTimeout(busyTimer);
      if (saveRequestId.current === requestId) setIsSaving(false);
    }
  };

  return (
    <Paper
      component="section"
      aria-labelledby="appearance-settings-title"
      sx={{
        mt: 4,
        p: { xs: 3, md: 4 },
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <Stack spacing={3}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
              color: 'primary.main',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
            }}
          >
            <PaletteIcon />
          </Box>
          <Box>
            <Typography id="appearance-settings-title" variant="h5" fontWeight={600}>
              {t('profile.appearance')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t('profile.appearanceDescription')}
            </Typography>
          </Box>
        </Box>

        <Box>
          <Typography
            id="appearance-theme-label"
            variant="caption"
            component="div"
            sx={{
              mb: 1.25,
              color: 'text.secondary',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            {t('profile.appearanceThemeLabel')}
          </Typography>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={handleThemeChange}
            disabled={isSaving}
            aria-labelledby="appearance-theme-label"
            sx={{
              width: '100%',
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
              '& .MuiToggleButtonGroup-grouped': {
                m: 0,
                border: '1px solid !important',
                borderColor: 'divider !important',
                borderRadius: '12px !important',
              },
            }}
          >
            {THEME_MODES.map((themeMode) => {
              const isLight = themeMode === 'light';
              const label = t(isLight ? 'profile.appearanceLight' : 'profile.appearanceDark');
              const description = t(
                isLight
                  ? 'profile.appearanceLightDescription'
                  : 'profile.appearanceDarkDescription',
              );
              const Icon = isLight ? LightModeIcon : DarkModeIcon;

              return (
                <ToggleButton
                  key={themeMode}
                  value={themeMode}
                  aria-label={label}
                  aria-describedby={`appearance-${themeMode}-description`}
                  sx={(theme) => ({
                    minWidth: 0,
                    p: 1.5,
                    alignItems: 'stretch',
                    justifyContent: 'flex-start',
                    color: 'text.primary',
                    textAlign: 'left',
                    textTransform: 'none',
                    transition: 'border-color 180ms ease, background-color 180ms ease, box-shadow 180ms ease',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                    },
                    '&.Mui-selected': {
                      color: 'text.primary',
                      borderColor: `${theme.palette.primary.main} !important`,
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.34)}`,
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                      },
                      '& [data-theme-preview]': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.shadows[3],
                      },
                    },
                  })}
                >
                  <Stack spacing={1.5} sx={{ width: '100%', minWidth: 0 }}>
                    <Box
                      data-theme-preview
                      sx={{
                        borderRadius: 2,
                        transition: 'transform 180ms ease, box-shadow 180ms ease',
                      }}
                    >
                      <ThemePreview themeMode={themeMode} />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                      <Icon sx={{ mt: 0.15, color: 'primary.main', fontSize: 21 }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography component="div" variant="subtitle2" fontWeight={700}>
                          {label}
                        </Typography>
                        <Typography
                          id={`appearance-${themeMode}-description`}
                          component="div"
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.25, lineHeight: 1.5 }}
                        >
                          {description}
                        </Typography>
                      </Box>
                    </Box>
                  </Stack>
                </ToggleButton>
              );
            })}
          </ToggleButtonGroup>
        </Box>

        {isSaving && (
          <Box
            role="status"
            aria-live="polite"
            sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}
          >
            <CircularProgress size={14} color="inherit" />
            <Typography variant="caption">{t('profile.appearanceSaving')}</Typography>
          </Box>
        )}

        {saveError && (
          <Alert severity="error" onClose={() => setSaveError(null)}>
            {saveError}
          </Alert>
        )}
      </Stack>
    </Paper>
  );
}
